import { createRoute, redirect } from '@tanstack/react-router';
import { FourOhFour } from '../components/404';
import { StoreLayoutComponent } from '../components/layouts/store-layout';
import { RouteError, StoreError } from '../components/route-errors';
import { TableSkeleton } from '../components/tanstack-table/table-skeleton';
import { can, StoreOperation } from '../data/permissions';
import type { Store } from '../data/store';
import { FetchWrapper } from '../helpers/fetch-wrapper';
import { getTabId } from '../hooks/use-tab-id';
import { AIAssistant } from '../pages/ai-assistant/index';
import { Dashboard } from '../pages/dashboard/index';
import { Products } from '../pages/products/index';
import { Edit } from '../pages/stores/edit/index';
import { Uploads } from '../pages/uploads/index';
import { EditVendor } from '../pages/vendors/edit';
import { Vendors } from '../pages/vendors/index';
import {
  companionConnectionsQueryOptions,
  productsQueryOptions,
  storesQueryOptions,
  uploadsQueryOptions,
  vendorsQueryOptions,
} from '../query/query-options';
import { classifyStores } from '../query/store-selectors';
import { ExceptionLogService } from '../services/exception-logs';
import { OrderService } from '../services/orders';
import { ProductService } from '../services/products';
import { ReceiptService } from '../services/receipts';
import { ReceivingService } from '../services/receiving';
import { UploadService } from '../services/uploads';
import { VendorService } from '../services/vendors';
import { authenticatedLayout } from './authenticated';

// ---------------------------------------------------------------------------
// Store layout (/stores/$storeId) — loads store, validates storeId
// ---------------------------------------------------------------------------

export const storeLayout = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: '/stores/$storeId',
  component: StoreLayoutComponent,
  pendingMs: 200,
  pendingMinMs: 300,
  errorComponent: StoreError,
  notFoundComponent: FourOhFour,
  beforeLoad: async ({ context, params }) => {
    const stores = await context.queryClient.ensureQueryData({
      ...storesQueryOptions(),
      queryFn: () => context.storeService.getAll(),
    });

    const store = stores.find((s: Store) => s.id === params.storeId);

    if (!store) {
      const { enabled, disabled } = classifyStores(stores);
      if (enabled.length > 0) {
        throw redirect({
          to: '/stores/$storeId',
          params: { storeId: enabled[0].id },
        });
      }

      if (disabled.length > 0) {
        throw redirect({
          to: '/onboard/$storeId',
          params: { storeId: disabled[0].id },
          search: { checkout: undefined },
        });
      }

      throw redirect({ to: '/onboard', search: { checkout: undefined } });
    }

    // Disabled store without subscription — checkout was never completed
    if (!store.enabled && !store.subscription_id) {
      throw redirect({
        to: '/onboard/$storeId',
        params: { storeId: store.id },
        search: { checkout: undefined },
      });
    }

    const tokenAccessor = context.auth.getAccessTokenSilently;
    const fetchWrapper = new FetchWrapper(tokenAccessor);
    const receivingService = new ReceivingService(fetchWrapper, getTabId());

    return {
      store,
      stores,
      receivingService,
      exceptionLogService: new ExceptionLogService(tokenAccessor),
      orderService: new OrderService(tokenAccessor),
      productService: new ProductService(tokenAccessor),
      receiptService: new ReceiptService(tokenAccessor),
      uploadService: new UploadService(tokenAccessor),
      vendorService: new VendorService(tokenAccessor),
    };
  },
});

// ---------------------------------------------------------------------------
// Store child routes
// ---------------------------------------------------------------------------

export const dashboardRoute = createRoute({
  getParentRoute: () => storeLayout,
  path: '/',
  component: Dashboard,
  // One guard rather than an edit at each of the eight places that navigate to
  // the bare store path: every one of them lands here, as does a typed URL or
  // a stale bookmark. productsRoute has no beforeLoad of its own, so it cannot
  // bounce back.
  beforeLoad: ({ context, params }) => {
    if (!can(context.store, StoreOperation.ReadDashboard)) {
      throw redirect({ to: '/stores/$storeId/products', params });
    }
  },
});

export const storeEditRoute = createRoute({
  getParentRoute: () => storeLayout,
  path: '/edit',
  component: Edit,
});

export const aiAssistantRoute = createRoute({
  getParentRoute: () => storeLayout,
  path: '/ai-assistant',
  component: AIAssistant,
});

export const vendorsRoute = createRoute({
  getParentRoute: () => storeLayout,
  path: '/vendors',
  component: Vendors,
  errorComponent: RouteError,
  pendingComponent: () => <TableSkeleton columnCount={10} />,
  loader: ({ context }) => {
    const { queryClient, productService, vendorService, store } = context;

    return Promise.all([
      queryClient.ensureQueryData({
        ...vendorsQueryOptions(store.id),
        queryFn: () => vendorService.getVendorsByStore(store.id),
      }),
      queryClient.ensureQueryData({
        ...productsQueryOptions(store.id),
        queryFn: () => productService.getProductsByStore(store.id),
      }),
    ]);
  },
});

export const vendorEditRoute = createRoute({
  getParentRoute: () => storeLayout,
  path: '/vendors/$vendorId/edit',
  component: EditVendor,
  errorComponent: RouteError,
});

export const productsRoute = createRoute({
  getParentRoute: () => storeLayout,
  path: '/products',
  component: Products,
  errorComponent: RouteError,
  pendingComponent: () => <TableSkeleton columnCount={14} />,
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      ...productsQueryOptions(context.store.id),
      queryFn: () => context.productService.getProductsByStore(context.store.id),
    }),
});

export const uploadsRoute = createRoute({
  getParentRoute: () => storeLayout,
  path: '/uploads',
  component: Uploads,
  errorComponent: RouteError,
  loader: ({ context }) => {
    const { queryClient, uploadService, store } = context;

    return Promise.all([
      queryClient.ensureQueryData({
        ...uploadsQueryOptions(store.id),
        queryFn: () => uploadService.getUploadsByStore(store.id),
      }),
      queryClient.ensureQueryData({
        ...companionConnectionsQueryOptions(store.id),
        queryFn: () => uploadService.getCompanionConnections(store.id),
      }),
    ]);
  },
});
